/**
 * Group Chat Display Metadata
 *
 * Normalizes sidebar-only group chat archive and stack metadata.
 *
 * Primary responsibilities:
 * - Read archived group chat state from chat metadata
 * - Read and merge group chat stack metadata
 * - Share the archived-groups UI storage contract
 *
 * Dependencies:
 * - Supabase generated chat table types
 */

import type { Json, Tables } from '@/supabase/types'

export const GROUP_CHAT_SHOW_ARCHIVED_STORAGE_KEY = 'groupChatsShowArchived'
export const GROUP_CHAT_SHOW_ARCHIVED_EVENT = 'group-chats-show-archived-change'

type MetadataRecord = Record<string, unknown>

function isRecord(value: unknown): value is MetadataRecord {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function metadataRecord(value: unknown): MetadataRecord {
  return isRecord(value) ? value : {}
}

function stringValue(value: unknown) {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function numberValue(value: unknown) {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

export function getGroupChatArchiveMetadata(metadata: unknown) {
  const groupChat = metadataRecord(metadataRecord(metadata).groupChat)
  const archive = metadataRecord(groupChat.archive)

  return {
    archivedAt: stringValue(archive.archivedAt),
    archivedBy: stringValue(archive.archivedBy)
  }
}

export function isGroupChatArchived(chat: Pick<Tables<'chats'>, 'metadata'>) {
  return Boolean(getGroupChatArchiveMetadata(chat.metadata).archivedAt)
}

export function mergeGroupChatArchiveMetadata(
  metadata: unknown,
  archive: {
    archivedAt: string | null
    archivedBy: string | null
  }
): Json {
  const root = metadataRecord(metadata)
  const groupChat = metadataRecord(root.groupChat)

  return {
    ...root,
    groupChat: {
      ...groupChat,
      archive
    }
  } as Json
}

export function getGroupChatStackMetadata(metadata: unknown) {
  const groupChat = metadataRecord(metadataRecord(metadata).groupChat)
  const stack = metadataRecord(groupChat.stack)

  return {
    count: numberValue(stack.count) ?? 1,
    key: stringValue(stack.key),
    label: stringValue(stack.label),
    parentChatId: stringValue(stack.parentChatId)
  }
}

export function getGroupChatStackKey(chat: Pick<Tables<'chats'>, 'id' | 'metadata'>) {
  return getGroupChatStackMetadata(chat.metadata).key ?? chat.id
}

export function getGroupChatStackCount(chat: Pick<Tables<'chats'>, 'metadata'>) {
  return Math.max(1, getGroupChatStackMetadata(chat.metadata).count)
}

export function mergeGroupChatStackMetadata(
  metadata: unknown,
  stack: {
    count?: number
    key: string
    label?: string | null
    parentChatId?: string | null
  }
): Json {
  const root = metadataRecord(metadata)
  const groupChat = metadataRecord(root.groupChat)

  return {
    ...root,
    groupChat: {
      ...groupChat,
      stack: {
        count: stack.count ?? 1,
        key: stack.key,
        label: stack.label ?? null,
        parentChatId: stack.parentChatId ?? null
      }
    }
  } as Json
}

export function withGroupChatStackCount<TChat extends Tables<'chats'>>(
  chat: TChat,
  count: number
) {
  return {
    ...chat,
    metadata: mergeGroupChatStackMetadata(chat.metadata, {
      ...getGroupChatStackMetadata(chat.metadata),
      count,
      key: getGroupChatStackKey(chat)
    })
  }
}
